import React, { useCallback, useState } from 'react';

import { buttons, colors, typography } from '@pray/shared/components/foundations';
import SocialAuth from '@pray/shared/components/SocialAuth/SocialAuth';
import Button from '@pray/shared/components/ui/Button/Button';
import InputField from '@pray/shared/components/ui/InputField/InputField';
import Text from '@pray/shared/components/ui/Text/Text';
import Container from '@pray/shared/components/v1/Container/Container';
import flowStyles from '@pray/shared/components/v1/FlowStyles.module.css';
import { EVENTS, PRAY_EXTERNAL_LINKS } from '@pray/shared/constants';
import SuccessCheckmark from '@pray/shared/icons/success-checkmark';
import logger from '@pray/shared/utils/logger';
import storage from '@pray/shared/utils/storage';
import { isValidEmail } from '@pray/shared/utils/validation';

import type { EmailCaptureProps } from './types';

import pageStyles from './EmailCapturePage.module.css';

const styles = {
  ...flowStyles,
  ...pageStyles,
};

const defaultTerms = (
  <>
    By providing your email and signing up, you agree to the{' '}
    <a href={PRAY_EXTERNAL_LINKS.TERMS_OF_SERVICE}>Terms&nbsp;of&nbsp;Service</a>,{' '}
    <a href={PRAY_EXTERNAL_LINKS.PRIVACY_POLICY}>Privacy Policy</a>, and confirm you are at least 16 years old.
  </>
);

export default function EmailCapturePage({
  title = "What's your email?",
  subtitle = 'Enter your email to login or sign up',
  data,
  terms = defaultTerms,
  showSocialAuth = true,
  handleEmailValidation,
  onDataChange,
  onAuthSuccess,
}: EmailCaptureProps) {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailValidating, setIsEmailValidating] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  let emailValidationTimeout: ReturnType<typeof setTimeout>;

  function validateData() {
    let validationError = '';

    if (!isValidEmail(data.email)) {
      validationError = 'Please enter a valid email address';
    }

    setError(validationError);
    return !validationError;
  }

  const handleEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    onDataChange('email', value);

    const isValid = isValidEmail(value);
    setIsFormValid(isValid);
    if (isValid) setError('');

    window.clearTimeout(emailValidationTimeout);

    setIsLoading(true);
    emailValidationTimeout = setTimeout(() => {
      setIsLoading(false);
      setError(isValid ? '' : 'Enter a valid email.');
    }, 1000);
  }, []);

  const handleContinue = async () => {
    if (isEmailValidating) return;
    try {
      setIsEmailValidating(true);
      const isDataValid = validateData();
      if (!isDataValid) {
        setIsEmailValidating(false);
        return;
      }

      let isEmailAvailable = true;

      const user = storage.getUser();
      if (data.email !== user?.email) {
        isEmailAvailable = await handleEmailValidation();
      }

      onDataChange('isEmailAvailable', isEmailAvailable);
    } catch (err) {
      logger.error('Failed to validate email', err);
    } finally {
      setIsEmailValidating(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isLoading) handleContinue();
  };

  return (
    <Container
      align=""
      className={pageStyles.container}
      data-viewname={EVENTS.PRAY_EVENTS.VIEW_EVENT_NAMES.LANDER_REDEEM_FLOW.EMAIL_CAPTURE}
    >
      <div className={styles.viewport}>
        <div className={styles.desktop}>{title && <Text variant={typography.heading_xl}>{title}</Text>}</div>

        <div className={styles.desktop}>
          {subtitle && (
            <Text variant={typography.headline_small} color={colors.text_secondary}>
              {subtitle}
            </Text>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.form}>
            <InputField
              autoFocus
              aria-label="email address"
              type="email"
              label="Email address"
              loading={isLoading}
              error={error}
              value={data.email}
              rightIcon={isFormValid && <SuccessCheckmark />}
              onChange={handleEmailChange}
            />
          </div>

          <Button
            full
            disabled={!isFormValid || isEmailValidating}
            loading={isEmailValidating}
            variant={buttons.variant.primary}
            onClick={handleContinue}
          >
            Continue
          </Button>

          {showSocialAuth && <SocialAuth onAuthSuccess={() => onAuthSuccess?.()} />}

          <div className={styles.desktop}>
            <div className={styles.terms}>{terms}</div>
          </div>
        </form>
      </div>
    </Container>
  );
}
